import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  HotelAdministrationStatus,
  InitiateDeletionRequest,
  SetShutdownRequest,
  HotelAdministrationResponse,
  DeletionPreflight,
} from "../types/hotelAdministration.types";

export interface HotelAdministrationServiceInterface {
  getStatus(): Promise<ApiResult<HotelAdministrationStatus>>;
  getDeletionPreflight(): Promise<ApiResult<DeletionPreflight>>;
  initiateDeletion(
    request: InitiateDeletionRequest,
  ): Promise<ApiResult<HotelAdministrationResponse>>;
  cancelDeletion(): Promise<ApiResult<HotelAdministrationResponse>>;
  setShutdown(
    request: SetShutdownRequest,
  ): Promise<ApiResult<HotelAdministrationResponse>>;
  disableShutdown(): Promise<ApiResult<HotelAdministrationResponse>>;
}

export class HotelAdministrationService
  extends BaseService
  implements HotelAdministrationServiceInterface
{
  /**
   * Get the current hotel administration status (deletion/shutdown state).
   */
  async getStatus(): Promise<ApiResult<HotelAdministrationStatus>> {
    return this.handleRequest<HotelAdministrationStatus>(
      this.client.get("/api/admin/hotel-administration/status"),
    );
  }

  /**
   * Get deletion preflight checklist status.
   */
  async getDeletionPreflight(): Promise<ApiResult<DeletionPreflight>> {
    return this.handleRequest<DeletionPreflight>(
      this.client.get("/api/admin/hotel-administration/deletion-preflight"),
    );
  }

  /**
   * Initiate hotel deletion with 72-hour grace period.
   * Only owners can initiate deletion.
   */
  async initiateDeletion(
    request: InitiateDeletionRequest,
  ): Promise<ApiResult<HotelAdministrationResponse>> {
    return this.handleRequest<HotelAdministrationResponse>(
      this.client.post("/api/admin/hotel-administration/delete", request),
    );
  }

  /**
   * Cancel a pending hotel deletion.
   * Only owners can cancel deletion.
   */
  async cancelDeletion(): Promise<ApiResult<HotelAdministrationResponse>> {
    return this.handleRequest<HotelAdministrationResponse>(
      this.client.post("/api/admin/hotel-administration/cancel-deletion"),
    );
  }

  /**
   * Set a temporary shutdown period for the hotel.
   * Only owners can set shutdown.
   */
  async setShutdown(
    request: SetShutdownRequest,
  ): Promise<ApiResult<HotelAdministrationResponse>> {
    return this.handleRequest<HotelAdministrationResponse>(
      this.client.post("/api/admin/hotel-administration/shutdown", request),
    );
  }

  /**
   * Disable the temporary shutdown.
   * Only owners can disable shutdown.
   */
  async disableShutdown(): Promise<ApiResult<HotelAdministrationResponse>> {
    return this.handleRequest<HotelAdministrationResponse>(
      this.client.delete("/api/admin/hotel-administration/shutdown"),
    );
  }
}
